import React, { useEffect, useMemo, useState } from 'react';
import SettingContainer from '../SettingContainer';
import { useConfig } from '../../../DataStore/ConfigContext';
import ActivityLoader from '../../Common/ActivityLoader';
import Notification from '../../Common/Notification';
import { useTheme } from '../../../DataStore/ThemeContext';
import { usePermission } from '../../../DataStore/PermissionContext';

/* ---------- Small helpers ---------- */
const roleIcon = (name = '') => {
  const n = String(name).toLowerCase();
  if (n.includes('super')) return 'fa-shield-halved';
  if (n.includes('admin')) return 'fa-user-shield';
  return 'fa-user';
};
const tintFromId = (id) => {
  const colors = ['#E3F2FD', '#E8F5E9', '#FFF3E0', '#F3E5F5', '#E0F7FA', '#FCE4EC', '#EDE7F6', '#E0F2F1'];
  const idx = Math.abs(parseInt(id, 10) || 0) % colors.length;
  return colors[idx];
};

const RoleCard = ({ role, active, onSelect, onEdit, onPermissions }) => {
  const bg = tintFromId(role.id);
  const themeColor = "#4F46E5";

  return (
    <div
      onClick={() => onSelect(role)}
      className="role-card-modern"
      style={{
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: `1px solid ${active ? themeColor : '#F1F5F9'}`, 
        borderRadius: '16px',
        backgroundColor: '#FFFFFF',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        position: 'relative',
        height: '100%',
        boxShadow: active ? '0 10px 25px -5px rgba(79, 70, 229, 0.1)' : '0 1px 2px rgba(0,0,0,0.02)'
      }}
    >
      {/* Icon Circle - Softer and slightly smaller */}
      <div
        className="mb-3 d-flex align-items-center justify-content-center"
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '12px',
          backgroundColor: bg,
          color: '#1E293B'
        }}
      >
        <i className={`fa ${roleIcon(role.role_name)}`} style={{ fontSize: '22px' }} />
      </div>

      <div className="mb-4">
        <div style={{ fontWeight: '600', fontSize: '16px', color: '#1E293B' }}>
          {role.role_name}
        </div>
        <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '4px' }}>
          ID: #{role.id}
        </div>
      </div>

      {/* Buttons: Using specific classes for hover effects */}
      <div className="mt-auto d-flex w-100" style={{ gap: '10px' }}>
        <button
          className="btn-permissions"
          onClick={(e) => { e.stopPropagation(); onPermissions(role); }}
        >
          <i className="fa fa-key mr-2" /> Permissions
        </button>
        <button
          className="btn-edit-icon"
          onClick={(e) => { e.stopPropagation(); onEdit(role); }}
        >
          <i className="fa fa-pen" />
        </button>
      </div>
    </div>
  );
};

const UserRoles = () => {
  const { openSettings } = useTheme();
  const [activeRecord, setActiveRecord] = useState(null);
  const [query, setQuery] = useState('');
  const { getUserRoles, userRoles, errorUserRoles, loadingUserRoles, dispatch } = useConfig();
  const {setConfigRole } = usePermission();

  useEffect(() => {
    getUserRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNew = () => openSettings('New User Role', 'ConfigNewUserRole', null, "Save Role");
  const handleEdit = (role = activeRecord) => role && openSettings('Edit User Role', 'ConfigEditUserRole', role);
//   const handlePermissions = (role = activeRecord) =>
//     role && openSettings(`${role.role_name} • Permissions`, 'ConfigRolePermissions', role);

   const handlePermissions = (role)=>{
        setConfigRole(role);
        dispatch({type: 'activeMenu', payload: 15});
    }


  const filtered = useMemo(() => {
    const list = Array.isArray(userRoles) ? userRoles : [];
    const q = query.trim().toLowerCase();
    const base = q ? list.filter((r) => r.role_name.toLowerCase().includes(q)) : list;
    // Aesthetic ordering: Super-Admin, Admin, then by name
    return [...base].sort((a, b) => {
      const rank = (n) => (n.toLowerCase().includes('super') ? 0 : n.toLowerCase().includes('admin') ? 1 : 2);
      const ra = rank(a.role_name);
      const rb = rank(b.role_name);
      return ra !== rb ? ra - rb : a.role_name.localeCompare(b.role_name);
    });
  }, [userRoles, query]);

  const menu = () => (
    <>
      <button type="button" tabIndex="0" className="dropdown-item" onClick={handleNew}>
        <i className="fa fa-plus" /> <span>New Role</span>
      </button>
      <button type="button" tabIndex="0" className="dropdown-item" disabled={!activeRecord} onClick={() => handleEdit()}>
        <i className="fa fa-edit" /> <span>Edit Selection</span>
      </button>
      <button type="button" tabIndex="0" className="dropdown-item" disabled={!activeRecord} onClick={() => handlePermissions()}>
        <i className="fa fa-key" /> <span>Manage Permissions</span>
      </button>
    </>
  );


  return (
    <SettingContainer title="User Roles" icon="fa fa-user-shield" Menu={menu}>
      {/* Re-styled Toolbar */}
      <div className="d-flex flex-column flex-sm-row align-items-sm-center px-4 py-3 border-bottom" style={{ gap: '15px', backgroundColor: '#FBFCFE' }}>
        <div className="input-group flex-nowrap" style={{ maxWidth: 360 }}>
          <div 
            className="input-group-prepend" 
            style={{ 
              backgroundColor: '#F1F5F9', 
              border: '1px solid #E2E8F0', 
              borderRight: 'none',
              borderRadius: '10px 0 0 10px',
              padding: '0 12px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <i className="fa fa-search text-muted" />
          </div>
          <input
            type="text"
            className="form-control"
            placeholder="Search roles..."
            style={{
              borderRadius: '0 10px 10px 0',
              border: '1px solid #E2E8F0',
              fontSize: '14px',
              height: '42px',
              boxShadow: 'none'
            }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        
        <button 
          className="btn ml-sm-auto shadow-none" 
          onClick={handleNew}
          style={{
            backgroundColor: "#4F46E5",
            color: 'white',
            borderRadius: '10px',
            fontWeight: '600',
            padding: '10px 20px',
            fontSize: '14px'
          }}
        >
          <i className="fa fa-plus mr-2" /> New Role
        </button>
      </div>

      {/* Content Area */}
      <div className="p-4 overflow-auto" style={{ maxHeight: '400px' }}>
        {errorUserRoles && <Notification type="danger" message={String(errorUserRoles)} />}
        
        {loadingUserRoles ? (
          <div className="py-5 text-center"><ActivityLoader /></div>
        ) : filtered.length > 0 ? (
          <div className="row">
            {filtered.map((role) => (
              <div className="col-lg-4 col-md-6 mb-4" key={role.id}>
                <RoleCard
                  role={role}
                  active={activeRecord?.id === role.id}
                  onSelect={setActiveRecord}
                  onEdit={handleEdit}
                  onPermissions={handlePermissions}
                />
              </div>
            ))}
          </div>
        ) : (
          /* Empty State - Modernized */
          <div className="text-center py-5">
            <div 
                className="d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#F8FAFC' }}
            >
                <i className="fa fa-users-cog text-muted" style={{ fontSize: '32px' }} />
            </div>
            <h6 style={{ fontWeight: '700', color: '#1A202C' }}>No roles found</h6>
            <p className="text-muted small">Try a different search or create a new role.</p>
          </div>
        )}
      </div>


      <style>{`
        .config-tile:hover {
            border-color: #4F46E5 !important;
            transform: translateY(-4px);
            box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.05);
        }
        .role-card-modern:hover {
          border-color: #4F46E5 !important;
          transform: translateY(-5px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05) !important;
        }

        /* Permissions Button: Starts subtle, turns solid on card hover */
        .btn-permissions {
          flex: 1;
          background-color: #F8FAFC;
          color: #4F46E5;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          padding: 8px 0;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .role-card-modern:hover .btn-permissions {
          background-color: #4F46E5;
          color: white;
          border-color: #4F46E5;
        }

        /* Edit Button: Smaller and more discreet */
        .btn-edit-icon {
          width: 40px;
          background-color: #F8FAFC;
          color: #64748B;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          transition: all 0.2s ease;
        }

        .role-card-modern:hover .btn-edit-icon {
          background-color: #FFFFFF;
          color: #1E293B;
        }
      `}</style>
    </SettingContainer>
  );
};

export default UserRoles;
